import { filter, map, pick, pipe, piped } from 'remeda';
import invariant from 'tiny-invariant';

import { DEFAULT_BLOCK } from '@@editor/constants';
import { Element } from '@@editor/helpers';
import {
    ELEMENT_TYPES,
    type ListElement,
    type ListItemElement,
    type ParagraphElement,
} from '@@editor/helpers/Element';
import { isUnityList, type UnityList, type UnityParagraph } from '@@editor/typings/UnityElements';
import toParagraph from '@@editor/utils/toParagraph';

import { type ElementRule, type Next } from '../types';
import { CURRENT_VERSION } from './../constants';
import textRules from './textRules';

const toListItem = (element: ParagraphElement): ListItemElement => ({
    ...element,
    type: ELEMENT_TYPES.LIST_ITEM,
});

const deserializeListItems = (next: Next<ParagraphElement>) => (items: UnityList['items']) =>
    pipe(items, map(piped(next, toListItem)));

const serializeListItems = (next: Next<UnityParagraph>) => (children: ListElement['children']) =>
    pipe(children, filter(Element.isListItemElement), map(piped(toParagraph, next)));

export const createListNode = <T extends ListElement>(
    type: T['type'],
    listItems: UnityParagraph[] = [],
    // TODO: [CD2-9344] Once we fix types, we can remove this `as any`
    next = textRules.deserialize as any,
) =>
    Element.create<T>({
        ...DEFAULT_BLOCK,
        type,
        children: deserializeListItems(next)(listItems),
    } as T);

type Rule = ElementRule<ListElement, UnityList>;

const listRules: Rule = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnityList(element)) {
            invariant(next, 'Cannot deserialize List without a `next` function');

            const { items, type } = element;

            return {
                type,
                data: pick(element, ['templateElement']),
                // TODO: [CD2-9344] Once we fix types, we could use `textRules.deserialize` here instead of `next`
                children: deserializeListItems(next)(items),
            };
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isListElement(node)) {
            invariant(next, 'Cannot serialize List without a `next` function');

            const { children, type } = node;

            return {
                version: CURRENT_VERSION,
                type,
                items: serializeListItems(next)(children),
            };
        }
    },
};

export default listRules;
